<template>
  <section class="change-history">
    <header class="change-history__header">
      <div>
        <h2>История изменений</h2>
        <p v-if="history.length" class="change-history__summary">
          Показано {{ visibleHistory.length }} из {{ history.length }}
        </p>
      </div>

      <button
        type="button"
        class="btn btn--outline-primary"
        :disabled="loading || !applicationId"
        @click="loadHistory"
      >
        Обновить
      </button>
    </header>

    <div v-if="loading" class="content content--center">
      <t-loader block text="Загружаем историю изменений..." />
    </div>

    <div v-else-if="!applicationId" class="card card--info">
      Заявление еще загружается. История изменений появится после загрузки данных.
    </div>

    <div v-else-if="!history.length" class="card card--muted">
      История изменений пуста.
    </div>

    <template v-else>
      <ol class="change-history__list">
        <li
          v-for="(entry, index) in visibleHistory"
          :key="`${entry.created_at}-${entry.item_type}-${entry.item_id}-${entry.action}-${index}`"
          class="change-history__item"
        >
          <article class="change-history__entry">
            <header class="change-history__entry-header">
              <div class="change-history__title-wrap">
                <span class="badge badge--pill" :class="actionBadgeClass(entry.action)">
                  {{ entry.action || 'UNKNOWN' }}
                </span>
                <h3 class="change-history__title">
                  {{ entry.item_type }}
                  <template v-if="entry.item_kind">, {{ entry.item_kind }}</template>
                  <template v-if="entry.item_id"> #{{ entry.item_id }}</template>
                </h3>
              </div>

              <div class="change-history__meta">
                <time>{{ entry.created_at }}</time>
                <span>{{ entry.user_full_name || 'System' }}</span>
              </div>
            </header>

            <dl v-if="Object.keys(entry.changes_info ?? {}).length" class="change-history__changes">
              <div
                v-for="[field, value] in Object.entries(entry.changes_info ?? {})"
                :key="field"
                class="change-history__change"
              >
                <dt class="change-history__field">{{ field }}</dt>
                <dd class="change-history__value">
                  <template v-if="isChange(value)">
                    <span class="change-history__old-value">{{ value.split(CHANGE_SEPARATOR)[0] }}</span>
                    <span class="change-history__arrow">→</span>
                    <span class="change-history__new-value">
                      {{ value.split(CHANGE_SEPARATOR).slice(1).join(CHANGE_SEPARATOR) }}
                    </span>
                  </template>
                  <template v-else>
                    {{ value }}
                  </template>
                </dd>
              </div>
            </dl>

            <p v-else class="change-history__empty-details">
              Нет детального описания изменений.
            </p>
          </article>
        </li>
      </ol>

      <button
        v-if="hasMore"
        type="button"
        class="btn btn--outline-primary change-history__more"
        @click="limit += PAGE_SIZE"
      >
        Показать еще {{ nextPageSize }}
      </button>
    </template>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useApplicationStore } from '@widgets/application-profile'
import { TLoader } from '@shared/ui'

const PAGE_SIZE = 20
const CHANGE_SEPARATOR = ' → '

const store = useApplicationStore()
const history = ref([])
const loading = ref(false)
const limit = ref(PAGE_SIZE)

const applicationId = computed(() => store.application?.id)
const visibleHistory = computed(() => history.value.slice(0, limit.value))
const hasMore = computed(() => limit.value < history.value.length)
const nextPageSize = computed(() => Math.min(PAGE_SIZE, history.value.length - limit.value))

watch(applicationId, loadHistory, { immediate: true })

async function loadHistory() {
  if (!applicationId.value) {
    history.value = []
    return
  }

  loading.value = true
  history.value = await store.getChangeHistory(applicationId.value)
  limit.value = PAGE_SIZE
  loading.value = false
}

function actionBadgeClass(action) {
  if (action === 'CREATE') return 'badge--solid-success'
  if (action === 'UPDATE') return 'badge--solid-primary'
  if (action === 'DESTROY') return 'badge--solid-danger'

  return 'badge--solid-muted'
}

function isChange(value) {
  return typeof value === 'string' && value.includes(CHANGE_SEPARATOR)
}
</script>
